import { Switch, Route, Redirect, useLocation } from "wouter";
import { Suspense, lazy, useMemo } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { DeferredToaster } from "@/components/deferred-toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider, ProtectedRoute } from "@/contexts/auth-context";
import { CustomerAuthProvider, CustomerProtectedRoute } from "@/contexts/customer-auth-context";
import { CrmEventsBusProvider } from "@/contexts/crm-events-bus";
import { LocalCartProvider } from "@/contexts/local-cart-context";
import { LayoutProvider } from "@/contexts/layout-context";
const CookieConsent = lazy(() => import("@/components/cookie-consent"));
const AnnouncementModal = lazy(() => import("@/components/announcement-modal").then(m => ({ default: m.AnnouncementModal })));
import { ErrorBoundary } from "@/components/error-boundary";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollToTop } from "@/components/scroll-to-top";
import { StylesProvider } from "@/components/styles-provider";
import { UtmTracker } from "@/components/utm-tracker";
import { MetaPixelTracker } from "@/components/meta-pixel-tracker";
import { PublicLayoutShell } from "@/components/public-layout-shell";
const CrmLayoutShell = lazy(() => import("@/components/crm/crm-layout-shell").then(m => ({ default: m.CrmLayoutShell })));

const Home = lazy(() => import("@/pages/home"));
const NotFound = lazy(() => import("@/pages/not-found"));

// Lazy load public pages for better performance
const ChiSiamo = lazy(() => import("@/pages/chi-siamo"));
const DoveSiamo = lazy(() => import("@/pages/dove-siamo"));
const Contatti = lazy(() => import("@/pages/contatti"));
const ThankYouPage = lazy(() => import("@/pages/thank-you"));
const NewsletterThankYouPage = lazy(() => import("@/pages/newsletter-thank-you"));
const VacationCouponPage = lazy(() => import("@/pages/vacation-coupon"));
const VacationCouponThankYouPage = lazy(() => import("@/pages/vacation-coupon-thank-you"));
const CercaPage = lazy(() => import("@/pages/cerca"));
const LavoraConNoi = lazy(() => import("@/pages/lavora-con-noi"));

// Lazy load Legal pages
const PrivacyPage = lazy(() => import("@/pages/privacy"));
const CookiePage = lazy(() => import("@/pages/cookie"));
const TerminiPage = lazy(() => import("@/pages/termini"));
const GaranziePage = lazy(() => import("@/pages/garanzie"));
const PoliticaResiPage = lazy(() => import("@/pages/politica-resi"));
const WhistleblowingPage = lazy(() => import("@/pages/whistleblowing"));
const CodiceEticoPage = lazy(() => import("@/pages/codice-etico"));
const Mog231Page = lazy(() => import("@/pages/mog-231"));
const SitemapPage = lazy(() => import("@/pages/sitemap"));
const SicurezzaTrasparenzaPage = lazy(() => import("@/pages/sicurezza-trasparenza"));

// Lazy load Orologeria section
const WatchCatalog = lazy(() => import("@/pages/watch-catalog"));
const WatchDetailPage = lazy(() => import("@/pages/watch-detail-page"));
const WatchValuationThankYouPage = lazy(() => import("@/pages/watch-valuation-thank-you"));
const WatchShop = lazy(() => import("@/pages/watch-shop"));
const WatchDetail = lazy(() => import("@/pages/watch-detail"));
const AppointmentConfirmationPage = lazy(() => import("@/pages/appointment-confirmation-page"));

// Lazy load Gioielleria section
const JewelryPage = lazy(() => import("@/pages/jewelry"));
const JewelryDetailPage = lazy(() => import("@/pages/jewelry-detail"));

// Lazy load Compro Oro section
const ComproOroValutazionePage = lazy(() => import("@/pages/compro-oro-valutazione"));
const PriceLockPage = lazy(() => import("@/pages/price-lock-page"));

// Lazy load Investimenti section
const ServicePage = lazy(() => import("@/pages/service"));
const InvestmentCatalog = lazy(() => import("@/pages/investment-catalog"));
const BancoMetalli = lazy(() => import("@/pages/banco-metalli"));
const ServiziPage = lazy(() => import("@/pages/servizi"));
const InvestmentDetailPage = lazy(() => import("@/pages/investment-detail-page"));

// Lazy load CRM Pages
const CrmLogin = lazy(() => import("@/pages/crm/crm-login"));
const ForgotPassword = lazy(() => import("@/pages/crm/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/crm/reset-password"));
const CrmDashboard = lazy(() => import("@/pages/crm/crm-dashboard"));
const CrmCustomers = lazy(() => import("@/pages/crm/crm-customers"));
const CrmCustomerImport = lazy(() => import("@/pages/crm/crm-customer-import"));
const CrmCustomerDetail = lazy(() => import("@/pages/crm/crm-customer-detail"));
const CrmLuxuryWatches = lazy(() => import("@/pages/crm/crm-luxury-watches"));
const CrmLuxuryWatchDetail = lazy(() => import("@/pages/crm/crm-luxury-watch-detail"));
const CrmInvestments = lazy(() => import("@/pages/crm/crm-investments"));
const CrmInvestmentDetail = lazy(() => import("@/pages/crm/crm-investment-detail"));
const CrmReports = lazy(() => import("@/pages/crm/crm-reports"));
const CrmAppointments = lazy(() => import("@/pages/crm/crm-appointments"));
const CrmSettings = lazy(() => import("@/pages/crm/crm-settings"));
const CrmSystemHealth = lazy(() => import("@/pages/crm/crm-system-health"));
const CrmPerformance = lazy(() => import("@/pages/crm/crm-performance"));
const CrmFinancing = lazy(() => import("@/pages/crm/crm-financing"));
const CrmMarketing = lazy(() => import("@/pages/crm/crm-marketing"));
const CrmContents = lazy(() => import("@/pages/crm/crm-contents"));
const CrmQuotations = lazy(() => import("@/pages/crm/crm-quotations"));
const CrmMetalPurities = lazy(() => import("@/pages/crm/crm-metal-purities"));
const CrmCoinsConfig = lazy(() => import("@/pages/crm/crm-coins-config"));
const CrmSeo = lazy(() => import("@/pages/crm/crm-seo"));
const CrmSeoControllo = lazy(() => import("@/pages/crm/crm-seo-controllo"));
const CrmJewelryListings = lazy(() => import("@/pages/crm/crm-jewelry-listings"));
const CrmJewelryDetail = lazy(() => import("@/pages/crm/crm-jewelry-detail"));
const CrmJewelryBrands = lazy(() => import("@/pages/crm/crm-jewelry-brands"));
const CrmWatchBrands = lazy(() => import("@/pages/crm/crm-watch-brands"));
const LuxuryWatchWizard = lazy(() => import("@/pages/crm/luxury-wizard"));
const JewelryWizard = lazy(() => import("@/pages/crm/jewelry-wizard"));
const CustomerWizard = lazy(() => import("@/pages/crm/customer-wizard"));
const CrmEcommerceCustomers = lazy(() => import("@/pages/crm/crm-ecommerce-customers"));
const CrmEcommerceOrders = lazy(() => import("@/pages/crm/crm-ecommerce-orders"));
const CrmStyles = lazy(() => import("@/pages/crm/crm-styles"));
const CrmImporter = lazy(() => import("@/pages/crm/crm-importer"));
const CrmEnrichment = lazy(() => import("@/pages/crm/crm-enrichment"));
const CrmWatchEnrichmentWizard = lazy(() => import("@/pages/crm/crm-watch-enrichment-wizard"));
const CrmInvestmentEnrichmentWizard = lazy(() => import("@/pages/crm/crm-investment-enrichment-wizard"));
const CrmPwaSettings = lazy(() => import("@/pages/crm/crm-pwa-settings"));
const CrmCameraCompanionSettings = lazy(() => import("@/pages/crm/camera-companion-settings"));
const CrmPhotoEditing = lazy(() => import("@/pages/crm/photo-editing"));
const CrmValuations = lazy(() => import("@/pages/crm/crm-valuations"));
const CrmWatchRequests = lazy(() => import("@/pages/crm/crm-watch-requests"));
const CrmHeroMedia = lazy(() => import("@/pages/crm/crm-hero-media"));
const CrmHeroSlides = lazy(() => import("@/pages/crm/crm-hero-slides"));
const CrmMediaLibrary = lazy(() => import("@/pages/crm/media-library"));
const CrmStaffProfiles = lazy(() => import("@/pages/crm/crm-staff-profiles"));
const CrmEmailManagement = lazy(() => import("@/pages/crm/email-management"));
const CrmEmailCampaigns = lazy(() => import("@/pages/crm/crm-email-campaigns"));
const CrmReviewsManagement = lazy(() => import("@/pages/crm/reviews-management"));
const CrmEmailInbox = lazy(() => import("@/pages/crm/crm-email-inbox"));
const CrmProfile = lazy(() => import("@/pages/crm/crm-profile"));
const ChangePasswordRequired = lazy(() => import("@/pages/crm/change-password-required"));
const CrmFacebookAds = lazy(() => import("@/pages/crm/crm-facebook-ads"));
const CrmPurchaseDeeds = lazy(() => import("@/pages/crm/crm-purchase-deeds"));
const CrmPurchaseDeedDetail = lazy(() => import("@/pages/crm/crm-purchase-deed-detail"));
const CrmPurchaseDeedsImport = lazy(() => import("@/pages/crm/crm-purchase-deeds-import"));
const CrmGeoAnalytics = lazy(() => import("@/pages/crm/crm-geo-analytics"));
const CrmPurchaseDeedsAnalytics = lazy(() => import("@/pages/crm/crm-purchase-deeds-analytics"));
const CrmDeedSettings = lazy(() => import("@/pages/crm/crm-deed-settings"));
const CrmNewPurchaseDeed = lazy(() => import("@/pages/crm/crm-new-purchase-deed"));
const CrmNewWatchDeed = lazy(() => import("@/pages/crm/crm-new-watch-deed"));
const CrmWatchDeedPrint = lazy(() => import("@/pages/crm/crm-watch-deed-print"));
const CrmDeedInventory = lazy(() => import("@/pages/crm/crm-deed-inventory"));
const CrmDeedPayments = lazy(() => import("@/pages/crm/crm-deed-payments"));
const CrmCareerApplications = lazy(() => import("@/pages/crm/crm-career-applications"));

// Lazy load Review Bonus Pages
const ReviewSignup = lazy(() => import("@/pages/review-signup"));
const ReviewThankYou = lazy(() => import("@/pages/review-thank-you"));
const ReviewFlyer = lazy(() => import("@/pages/review-flyer"));
const ReviewSignupAcquisti = lazy(() => import("@/pages/review-signup-acquisti"));
const ReviewThankYouAcquisti = lazy(() => import("@/pages/review-thank-you-acquisti"));
const ReviewFlyerAcquisti = lazy(() => import("@/pages/review-flyer-acquisti"));

// Lazy load Test Pages
const TestOverride = lazy(() => import("@/pages/test-override"));
const TestWarranty = lazy(() => import("@/pages/test-warranty"));

// Lazy load Landing Pages (Google Ads)
const WatchValuationLanding = lazy(() => import("@/pages/lp/watch-valuation-progressive"));
const WatchValuationThanks = lazy(() => import("@/pages/lp/watch-valuation-thanks"));
const WatchValuationPhotos = lazy(() => import("@/pages/lp/watch-valuation-photos"));
const WatchValuationInfo = lazy(() => import("@/pages/lp/watch-valuation-info"));
const WatchProposalResponse = lazy(() => import("@/pages/lp/watch-proposal-response"));
const WatchDeliveryChoice = lazy(() => import("@/pages/lp/watch-delivery-choice"));
const WatchShipmentTracking = lazy(() => import("@/pages/lp/watch-shipment-tracking"));

// Lazy load Camera Companion (PWA for smartphone camera capture)
const CameraCompanion = lazy(() => import("@/pages/camera-companion"));

// Lazy load Customer E-commerce Pages
const CustomerLogin = lazy(() => import("@/pages/customer-login"));
const CustomerRegister = lazy(() => import("@/pages/customer-register"));
const CustomerForgotPassword = lazy(() => import("@/pages/customer-forgot-password"));
const CustomerResetPassword = lazy(() => import("@/pages/customer-reset-password"));
const CustomerCreatePassword = lazy(() => import("@/pages/customer-create-password"));
const VerificaEmail = lazy(() => import("@/pages/verifica-email"));
const CartPage = lazy(() => import("@/pages/cart"));
const CheckoutWizard = lazy(() => import("@/pages/checkout-wizard"));
const CheckoutPage = lazy(() => import("@/pages/checkout"));
const OrderConfirmationPage = lazy(() => import("@/pages/order-confirmation-page"));
const CustomerAreaLayout = lazy(() => import("@/pages/customer-area"));

// Page loading skeleton
function PageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-12 w-64 mb-8" />
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

// Captured at initial document load, not recalculated on client navigation.
const bootNotFoundPath = typeof document !== "undefined" &&
  document.querySelector('meta[name="uo-http-not-found"]')
  ? window.location.pathname
  : null;

function Router() {
  const [location] = useLocation();
  // Keep the HTTP 404 decision after React replaces the server shell.
  // Navigation away from this URL proceeds normally.
  if (bootNotFoundPath && location === bootNotFoundPath) {
    return <NotFound />;
  }
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ScrollToTop />
      <Switch>
        {/* Homepage */}
        <Route path="/" component={Home} />
        
        {/* ============================================= */}
        {/* OROLOGERIA SECTION - New SEO-friendly URLs */}
        {/* ============================================= */}
        
        {/* Landing page */}
        <Route path="/orologeria">{() => <WatchCatalog />}</Route>
        
        {/* Catalog / In Vendita */}
        <Route path="/orologeria/in-vendita/:anno/:marca/:modello/:referenza/:condizione/:codice">
          {() => <WatchDetailPage />}
        </Route>
        <Route path="/orologeria/in-vendita/page/:page">{() => <WatchCatalog />}</Route>
        <Route path="/orologeria/in-vendita/:marca/page/:page">{() => <WatchCatalog />}</Route>
        <Route path="/orologeria/in-vendita/:marca">{() => <WatchCatalog />}</Route>
        <Route path="/orologeria/in-vendita">{() => <WatchCatalog />}</Route>
        
        {/* Valutazione / Vendita orologi */}
        <Route path="/orologeria/valutazione/grazie">{() => <WatchValuationThankYouPage />}</Route>
        <Route path="/orologeria/valutazione">{() => <WatchValuationLanding />}</Route>
        <Route path="/orologeria/valutazione/:marca">{() => <WatchValuationLanding />}</Route>
        <Route path="/orologeria/valutazione/:marca/:modello">{() => <WatchValuationLanding />}</Route>
        
        {/* Brand-specific selling pages */}
        <Route path="/orologeria/vendi/:brand">{() => <WatchValuationLanding />}</Route>
        
        {/* ============================================= */}
        {/* GIOIELLERIA SECTION */}
        {/* ============================================= */}
        
        {/* Landing / Catalog */}
        <Route path="/gioielleria" component={JewelryPage} />
        
        {/* Category pages */}
        <Route path="/gioielleria/anelli">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/bracciali">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/collane">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/orecchini">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/spille">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/pendenti">{() => <JewelryPage />}</Route>
        <Route path="/gioielleria/gemelli">{() => <JewelryPage />}</Route>
        
        {/* Detail pages */}
        <Route path="/gioielleria/:categoria/:materiale/:codice">{() => <JewelryDetailPage />}</Route>
        <Route path="/gioielleria/:id" component={JewelryDetailPage} />
        
        {/* ============================================= */}
        {/* COMPRO ORO E ARGENTO SECTION */}
        {/* ============================================= */}
        
        {/* Landing page */}
        <Route path="/compro-oro-argento">{() => <ComproOroValutazionePage />}</Route>
        
        {/* Valutazione */}
        <Route path="/compro-oro-argento/valutazione">{() => <ComproOroValutazionePage />}</Route>
        <Route path="/compro-oro-argento/valutazione/:citta">{() => <ComproOroValutazionePage />}</Route>
        <Route path="/compro-oro-argento/valutazione/:citta/:metallo/:titolo">{() => <ComproOroValutazionePage />}</Route>
        
        {/* Testimonianze */}
        <Route path="/compro-oro-argento/testimonianze">{() => <Redirect to="/compro-oro-argento" />}</Route>
        
        {/* Blocca Prezzo */}
        <Route path="/compro-oro/blocca-prezzo">{() => <PriceLockPage />}</Route>
        
        {/* ============================================= */}
        {/* INVESTIRE IN ORO SECTION */}
        {/* ============================================= */}
        
        {/* Landing page - now shows catalog directly */}
        <Route path="/investire-in-oro">{() => <InvestmentCatalog />}</Route>
        
        {/* Catalog / E-commerce - MUST be before :id route */}
        <Route path="/investire-in-oro/catalogo">{() => <InvestmentCatalog />}</Route>
        
        {/* Lingotti - MUST be before :id route */}
        <Route path="/investire-in-oro/lingotti">{() => <InvestmentCatalog />}</Route>
        <Route path="/investire-in-oro/lingotti/:raffineria/:peso">{() => <ServicePage />}</Route>
        
        {/* Monete - MUST be before :id route */}
        <Route path="/investire-in-oro/monete">{() => <InvestmentCatalog />}</Route>
        <Route path="/investire-in-oro/monete/:paese/:taglio">{() => <ServicePage />}</Route>
        
        {/* Investment detail page - MUST be LAST (catch-all for IDs) */}
        <Route path="/investire-in-oro/:id">{() => <InvestmentDetailPage />}</Route>
        
        {/* ============================================= */}
        {/* SERVIZI HUB */}
        {/* ============================================= */}
        <Route path="/servizi">{() => <ServiziPage />}</Route>

        {/* ============================================= */}
        {/* BANCO METALLI */}
        {/* ============================================= */}
        <Route path="/banco-metalli">{() => <BancoMetalli />}</Route>

        {/* ============================================= */}
        {/* INFO PAGES */}
        {/* ============================================= */}
        <Route path="/chi-siamo" component={ChiSiamo} />
        <Route path="/dove-siamo" component={DoveSiamo} />
        <Route path="/contatti" component={Contatti} />
        <Route path="/lavora-con-noi" component={LavoraConNoi} />
        <Route path="/thank-you" component={ThankYouPage} />
        <Route path="/cerca" component={CercaPage} />
        <Route path="/newsletter/grazie" component={NewsletterThankYouPage} />
        {/* Modalità ferie — buono 100€ (Task #446) */}
        <Route path="/ferie" component={VacationCouponPage} />
        <Route path="/ferie/grazie" component={VacationCouponThankYouPage} />
        <Route path="/conferma-appuntamento/:token" component={AppointmentConfirmationPage} />
        
        {/* Review Bonus Pages - Compro Oro (vendita oro +10€ bonus) */}
        <Route path="/recensione" component={ReviewSignup} />
        <Route path="/recensione/grazie" component={ReviewThankYou} />
        <Route path="/recensione/volantino" component={ReviewFlyer} />
        
        {/* Review Bonus Pages - Acquisti Gioielli/Orologi (-50€ sconto) */}
        <Route path="/recensione-acquisti" component={ReviewSignupAcquisti} />
        <Route path="/recensione-acquisti/grazie" component={ReviewThankYouAcquisti} />
        <Route path="/recensione-acquisti/volantino" component={ReviewFlyerAcquisti} />
        
        {/* Legal pages */}
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/cookie" component={CookiePage} />
        <Route path="/termini" component={TerminiPage} />
        <Route path="/garanzie" component={GaranziePage} />
        <Route path="/sicurezza-trasparenza" component={SicurezzaTrasparenzaPage} />
        <Route path="/politica-resi" component={PoliticaResiPage} />
        <Route path="/whistleblowing" component={WhistleblowingPage} />
        <Route path="/codice-etico" component={CodiceEticoPage} />
        <Route path="/mog-231" component={Mog231Page} />
        <Route path="/mappa-del-sito" component={SitemapPage} />
        
        {/* ============================================= */}
        {/* LEGACY REDIRECTS (301) */}
        {/* ============================================= */}
        
        {/* Old service pages */}
        <Route path="/servizio/orologeria">{() => <Redirect to="/orologeria" />}</Route>
        <Route path="/servizio/compro-oro">{() => <Redirect to="/compro-oro-argento" />}</Route>
        <Route path="/servizio/investimenti">{() => <Redirect to="/investire-in-oro" />}</Route>
        
        {/* Old watch pages */}
        <Route path="/vendere-comprare-orologi-lusso-roma">{() => <Redirect to="/orologeria/in-vendita" />}</Route>
        <Route path="/watch-shop">{() => <Redirect to="/orologeria/in-vendita" />}</Route>
        
        {/* Watch detail pages - all use unified WatchDetailPage */}
        <Route path="/orologi/:id">{() => <WatchDetailPage />}</Route>
        <Route path="/watch/:id">{() => <WatchDetailPage />}</Route>
        
        {/* Old compro oro pages */}
        <Route path="/compro-oro-argento-roma-valutazione">{() => <Redirect to="/compro-oro-argento/valutazione" />}</Route>
        
        {/* Old investment pages */}
        <Route path="/lingotti-oro-monete-investimento">{() => <Redirect to="/investire-in-oro" />}</Route>
        
        {/* Old brand selling pages */}
        <Route path="/vendi-rolex">{() => <Redirect to="/orologeria/vendi/rolex" />}</Route>
        <Route path="/vendi-omega">{() => <Redirect to="/orologeria/vendi/omega" />}</Route>
        <Route path="/vendi-patek-philippe">{() => <Redirect to="/orologeria/vendi/patek-philippe" />}</Route>
        <Route path="/vendi-audemars-piguet">{() => <Redirect to="/orologeria/vendi/audemars-piguet" />}</Route>
        <Route path="/vendi-cartier">{() => <Redirect to="/orologeria/vendi/cartier" />}</Route>
        <Route path="/vendi-iwc">{() => <Redirect to="/orologeria/vendi/iwc" />}</Route>
        
        {/* ============================================= */}
        {/* CUSTOMER E-COMMERCE ROUTES */}
        {/* ============================================= */}
        <Route path="/accedi" component={CustomerLogin} />
        <Route path="/cliente/login" component={CustomerLogin} />
        <Route path="/password-dimenticata" component={CustomerForgotPassword} />
        <Route path="/forgot-password" component={CustomerForgotPassword} />
        <Route path="/reset-password" component={CustomerResetPassword} />
        <Route path="/crea-password" component={CustomerCreatePassword} />
        <Route path="/registrati" component={CustomerRegister} />
        <Route path="/verifica-email" component={VerificaEmail} />
        <Route path="/carrello" component={CartPage} />
        <Route path="/checkout" component={CheckoutPage} />
        <Route path="/ordine-confermato" component={OrderConfirmationPage} />
        <Route path="/area-clienti/:rest*" component={CustomerAreaLayout} />
        <Route path="/area-clienti" component={CustomerAreaLayout} />
        
        {/* ============================================= */}
        {/* TEST PAGES */}
        {/* ============================================= */}
        <Route path="/test-override" component={TestOverride} />
        <Route path="/test-warranty" component={TestWarranty} />
        
        {/* ============================================= */}
        {/* LANDING PAGES (Google Ads) */}
        {/* ============================================= */}
        <Route path="/lp/valutazione-orologi" component={WatchValuationLanding} />
        <Route path="/lp/valutazione-orologi/grazie" component={WatchValuationThanks} />
        <Route path="/lp/valutazione-orologi/foto" component={WatchValuationPhotos} />
        <Route path="/lp/valutazione-orologi/info" component={WatchValuationInfo} />
        <Route path="/lp/valutazione-orologi/proposta" component={WatchProposalResponse} />
        <Route path="/lp/valutazione-orologi/consegna" component={WatchDeliveryChoice} />
        <Route path="/valutazione/spedizione/:token" component={WatchShipmentTracking} />
        
        {/* ============================================= */}
        {/* CAMERA COMPANION PWA */}
        {/* ============================================= */}
        <Route path="/camera-companion" component={CameraCompanion} />
        
        {/* ============================================= */}
        {/* CRM ROUTES - Protected with JWT authentication */}
        {/* ============================================= */}
        <Route path="/crm-secure/login" component={CrmLogin} />
        <Route path="/crm-secure/forgot-password" component={ForgotPassword} />
        <Route path="/crm-secure/reset-password" component={ResetPassword} />
        <Route path="/crm-secure/change-password-required">
          {() => (
            <ProtectedRoute>
              <ChangePasswordRequired />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/crm-secure/dashboard">
          {() => (
            <ProtectedRoute>
              <CrmDashboard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/customers/new/:step*">
          {() => (
            <ProtectedRoute>
              <CustomerWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/customers/new">
          {() => (
            <ProtectedRoute>
              <CustomerWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/customers/import">
          {() => (
            <ProtectedRoute>
              <CrmCustomerImport />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/customers/:id">
          {() => (
            <ProtectedRoute>
              <CrmCustomerDetail />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/customers">
          {() => (
            <ProtectedRoute>
              <CrmCustomers />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/candidature">
          {() => (
            <ProtectedRoute>
              <CrmCareerApplications />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/luxury-watches/new/:step*">
          {() => (
            <ProtectedRoute>
              <LuxuryWatchWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/luxury-watches/:id">
          {() => (
            <ProtectedRoute>
              <CrmLuxuryWatchDetail />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/luxury-watches">
          {() => (
            <ProtectedRoute>
              <CrmLuxuryWatches />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/valuations">
          {() => (
            <ProtectedRoute>
              <CrmValuations />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/richieste">
          {() => (
            <ProtectedRoute>
              <CrmWatchRequests />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/jewelry-listings/new/:step">
          {() => (
            <ProtectedRoute>
              <JewelryWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/jewelry-listings/new">
          {() => (
            <ProtectedRoute>
              <JewelryWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/jewelry-listings/:id">
          {() => (
            <ProtectedRoute>
              <CrmJewelryDetail />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/jewelry-listings">
          {() => (
            <ProtectedRoute>
              <CrmJewelryListings />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/jewelry-brands">
          {() => (
            <ProtectedRoute>
              <CrmJewelryBrands />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/watch-brands">
          {() => (
            <ProtectedRoute>
              <CrmWatchBrands />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/facebook-ads">
          {() => (
            <ProtectedRoute>
              <CrmFacebookAds />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/watch/new">
          {() => (
            <ProtectedRoute>
              <CrmNewWatchDeed />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/nuovo">
          {() => (
            <ProtectedRoute>
              <CrmNewPurchaseDeed />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/settings">
          {() => (
            <ProtectedRoute>
              <CrmDeedSettings />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/analytics">
          {() => (
            <ProtectedRoute>
              <CrmPurchaseDeedsAnalytics />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/import">
          {() => (
            <ProtectedRoute>
              <CrmPurchaseDeedsImport />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/:id/print">
          {() => (
            <ProtectedRoute>
              <CrmWatchDeedPrint />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds/:id">
          {() => (
            <ProtectedRoute>
              <CrmPurchaseDeedDetail />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/purchase-deeds">
          {() => (
            <ProtectedRoute>
              <CrmPurchaseDeeds />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/deed-inventory">
          {() => (
            <ProtectedRoute>
              <CrmDeedInventory />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/deed-payments">
          {() => (
            <ProtectedRoute>
              <CrmDeedPayments />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/prodotti-finanziari/:id">
          {() => (
            <ProtectedRoute>
              <CrmInvestmentDetail />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/prodotti-finanziari">
          {() => (
            <ProtectedRoute>
              <CrmInvestments />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/reports">
          {() => (
            <ProtectedRoute requiredRole="manager">
              <CrmReports />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/appointments">
          {() => (
            <ProtectedRoute>
              <CrmAppointments />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/marketing">
          {() => (
            <ProtectedRoute>
              <CrmMarketing />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/email-campaigns">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmEmailCampaigns />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/geo-analytics">
          {() => (
            <ProtectedRoute requiredRole="manager">
              <CrmGeoAnalytics />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/contents">
          {() => (
            <ProtectedRoute>
              <CrmContents />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/quotations">
          {() => (
            <ProtectedRoute>
              <CrmQuotations />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/metal-purities">
          {() => (
            <ProtectedRoute>
              <CrmMetalPurities />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/coins-config">
          {() => (
            <ProtectedRoute>
              <CrmCoinsConfig />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/staff-profiles">
          {() => (
            <ProtectedRoute>
              <CrmStaffProfiles />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/email">
          {() => (
            <ProtectedRoute>
              <CrmEmailManagement />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/reviews">
          {() => (
            <ProtectedRoute>
              <CrmReviewsManagement />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/email-inbox">
          {() => (
            <ProtectedRoute>
              <CrmEmailInbox />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/seo">
          {() => (
            <ProtectedRoute>
              <CrmSeo />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/seo-controllo">
          {() => (
            <ProtectedRoute>
              <CrmSeoControllo />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/styles">
          {() => (
            <ProtectedRoute>
              <CrmStyles />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/importer">
          {() => (
            <ProtectedRoute>
              <CrmImporter />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/enrichment/watch/:id">
          {() => (
            <ProtectedRoute>
              <CrmWatchEnrichmentWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/enrichment/investment/:id">
          {() => (
            <ProtectedRoute>
              <CrmInvestmentEnrichmentWizard />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/enrichment">
          {() => (
            <ProtectedRoute>
              <CrmEnrichment />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/profile">
          {() => (
            <ProtectedRoute>
              <CrmProfile />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/settings">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmSettings />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/sistema-salute">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmSystemHealth />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/performance">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmPerformance />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/financing">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmFinancing />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/pwa-settings">
          {() => (
            <ProtectedRoute requiredRole="admin">
              <CrmPwaSettings />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/camera-companion">
          {() => (
            <ProtectedRoute>
              <CrmCameraCompanionSettings />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/photo-editing">
          {() => (
            <ProtectedRoute>
              <CrmPhotoEditing />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/hero-media">
          {() => (
            <ProtectedRoute>
              <CrmHeroMedia />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/media">
          {() => (
            <ProtectedRoute>
              <CrmMediaLibrary />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/hero-slides">
          {() => (
            <ProtectedRoute>
              <CrmHeroSlides />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/clienti-ecommerce">
          {() => (
            <ProtectedRoute>
              <CrmEcommerceCustomers />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/ordini-ecommerce/:id*">
          {() => (
            <ProtectedRoute>
              <CrmEcommerceOrders />
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/crm-secure/ordini-ecommerce">
          {() => (
            <ProtectedRoute>
              <CrmEcommerceOrders />
            </ProtectedRoute>
          )}
        </Route>
        
        {/* Redirect from /crm-secure to login */}
        <Route path="/crm-secure">
          {() => <Redirect to="/crm-secure/login" />}
        </Route>
        
        {/* 404 */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function LayoutWrapper() {
  const [location] = useLocation();
  
  const layoutType = useMemo(() => {
    // Keep protected-route layout detection case-insensitive. Browsers and
    // bookmarks may preserve `/CRM-secure/...`; that must never receive the
    // public header/footer even if the router still resolves the page.
    const normalizedLocation = location.toLowerCase();
    if (normalizedLocation.startsWith('/crm-secure/login') || 
        normalizedLocation.startsWith('/crm-secure/forgot-password') ||
        normalizedLocation.startsWith('/crm-secure/reset-password')) {
      return 'none';
    }
    if (normalizedLocation.startsWith('/crm-secure')) {
      return 'crm';
    }
    if (normalizedLocation.startsWith('/lp/') || 
        normalizedLocation.startsWith('/camera-companion') ||
        normalizedLocation.startsWith('/recensione/volantino') ||
        normalizedLocation.startsWith('/recensione-acquisti/volantino') ||
        normalizedLocation.startsWith('/valutazione/spedizione/')) {
      return 'none';
    }
    return 'public';
  }, [location]);

  if (layoutType === 'crm') {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <CrmLayoutShell>
          <Router />
        </CrmLayoutShell>
      </Suspense>
    );
  }
  
  if (layoutType === 'public') {
    return (
      <PublicLayoutShell>
        <Router />
      </PublicLayoutShell>
    );
  }
  
  return <Router />;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <StylesProvider>
            <LayoutProvider>
              <AuthProvider>
                <CrmEventsBusProvider>
                <CustomerAuthProvider>
                  <LocalCartProvider>
                    <TooltipProvider>
                      <ErrorBoundary>
                        <UtmTracker />
                        <MetaPixelTracker />
                        <LayoutWrapper />
                        <DeferredToaster />
                        <Suspense fallback={null}>
                          <CookieConsent />
                        </Suspense>
                        <Suspense fallback={null}>
                          <AnnouncementModal />
                        </Suspense>
                      </ErrorBoundary>
                    </TooltipProvider>
                  </LocalCartProvider>
                </CustomerAuthProvider>
                </CrmEventsBusProvider>
              </AuthProvider>
            </LayoutProvider>
          </StylesProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
